// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/DavImpressaoTermica.tsx
//  Layout otimizado para impressora térmica 80mm. Abre em nova aba e dispara
//  window.print() automaticamente quando os dados estão prontos.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { davService } from '@/features/operacional/services/davService';
import type { DavDetalhe } from '@/features/operacional/types';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const NUM = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const DATA = new Intl.DateTimeFormat('pt-BR');

function formatarData(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : DATA.format(d);
}

export default function DavImpressaoTermica() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const viaParam = Number.parseInt(searchParams.get('via') ?? '1', 10);
  const via      = Number.isFinite(viaParam) && viaParam >= 1 ? viaParam : 1;

  const { data: dav, isLoading, error } = useQuery({
    queryKey: ['dav', id, 'termica', via],
    queryFn:  () => davService.getById(Number(id)),
    enabled:  !!id,
  });

  // Dispara impressão automaticamente quando terminar de carregar
  useEffect(() => {
    if (dav) {
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
  }, [dav]);

  const situacao = useMemo(() => {
    if (!dav) return '';
    if (dav.isEstornado) return 'ESTORNADO';
    if (dav.isBaixada)   return 'BAIXADO';
    return 'EM ABERTO';
  }, [dav]);

  if (!id) return <div className="p-4">Parâmetro ausente.</div>;
  if (isLoading) return <div className="p-4">Carregando...</div>;
  if (error || !dav) return <div className="p-4">Não foi possível carregar o DAV.</div>;

  return (
    <>
      <style>{cssTermica}</style>
      <div className="dav-termica">
        {/* Cabeçalho */}
        {via >= 2 && (
          <div className="via-badge center bold">★ {via}ª VIA ★</div>
        )}
        <div className="center bold">DAV</div>
        <div className="center">Nº {dav.numeroDav}</div>
        <div className="center">Emissão: {formatarData(dav.dtEmissao)}</div>
        <div className="center bold mt-2">{situacao}</div>
        {dav.isEstornado && dav.dtEstorno && (
          <div className="center small">Estornado em {formatarData(dav.dtEstorno)}</div>
        )}

        <div className="divider" />

        {/* Cliente */}
        <div className="bold">CLIENTE</div>
        <div>{dav.nomeRazao ?? (dav.clienteId ? `Cliente #${dav.clienteId}` : 'Consumidor final')}</div>
        {dav.cpfCnpj && <div>CPF/CNPJ: {dav.cpfCnpj}</div>}
        {(dav.fone || dav.celular) && (
          <div>Contato: {[dav.fone, dav.celular].filter(Boolean).join(' / ')}</div>
        )}

        <div className="divider" />

        {/* Itens */}
        <div className="bold">PRODUTOS</div>
        {dav.produtos.map((p, i) => (
          <div key={p.id} className="item">
            <div>{i + 1}. Produto #{p.produtoId}</div>
            <div className="row">
              <span>{NUM.format(p.qtdMovimento)} × {BRL.format(p.valorUnitario)}</span>
              <span className="bold">{BRL.format(p.valorTotal)}</span>
            </div>
          </div>
        ))}

        <div className="divider" />

        {/* Totais */}
        <TotalLinha label="Total bruto"     valor={dav.totalGeral ?? 0} />
        <TotalLinha label="(-) Descontos"   valor={dav.totalDesconto ?? 0} />
        <TotalLinha label="(+) Acréscimos"  valor={dav.totalAcrescimo ?? 0} />
        <div className="divider" />
        <div className="row bold big">
          <span>TOTAL</span>
          <span>{BRL.format(dav.totalLiquido ?? 0)}</span>
        </div>

        {/* Observações */}
        {dav.observacao && (
          <>
            <div className="divider" />
            <div className="bold">OBSERVAÇÕES</div>
            <div className="small">{dav.observacao}</div>
          </>
        )}

        <div className="divider" />
        <div className="center small">Obrigado pela preferência!</div>

        {/* Botões visíveis só na tela (no print somem) */}
        <div className="actions no-print">
          <button onClick={() => window.print()}>Imprimir</button>
          <button onClick={() => window.close()}>Fechar</button>
        </div>
      </div>
    </>
  );
}

function TotalLinha({ label, valor }: { label: string; valor: number }) {
  return (
    <div className="row">
      <span>{label}</span>
      <span>{BRL.format(valor)}</span>
    </div>
  );
}

// CSS inline — mantém o arquivo auto-contido e isolado de estilos globais.
const cssTermica = `
.dav-termica {
  width: 72mm;
  margin: 0 auto;
  padding: 4mm 2mm;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  line-height: 1.35;
  color: #000;
}
.dav-termica .center { text-align: center; }
.dav-termica .bold { font-weight: 700; }
.dav-termica .small { font-size: 10px; }
.dav-termica .big { font-size: 13px; }
.dav-termica .mt-2 { margin-top: 6px; }
.dav-termica .divider {
  border-top: 1px dashed #000;
  margin: 6px 0;
}
.dav-termica .via-badge {
  border: 2px solid #000;
  padding: 2px 4px;
  margin-bottom: 6px;
  font-size: 13px;
  letter-spacing: 2px;
}
.dav-termica .row {
  display: flex;
  justify-content: space-between;
  gap: 6px;
}
.dav-termica .item {
  margin-bottom: 4px;
}
.dav-termica .actions {
  margin-top: 16px;
  display: flex;
  gap: 8px;
  justify-content: center;
}
.dav-termica .actions button {
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
}

@media print {
  @page {
    size: 80mm auto;
    margin: 0;
  }
  body {
    margin: 0;
    padding: 0;
  }
  .no-print {
    display: none !important;
  }
  .dav-termica {
    width: 76mm;
    padding: 2mm;
  }
}
`;
